import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiX, FiShoppingCart } from 'react-icons/fi';
import ProductCard from '../components/ProductCard';
import { useFavorites } from '../hooks/useFavorites';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px 20px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 32px;
`;

const EmptyWishlist = styled.div`
  text-align: center;
  padding: 64px 24px;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 24px;
`;

const EmptyTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 12px;
`;

const EmptyText = styled.p`
  color: #6b7280;
  margin-bottom: 32px;
`;

const ShopButton = styled(Link)`
  display: inline-block;
  padding: 12px 32px;
  background: #34D399;
  color: white;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 24px;
`;

function Wishlist() {
  const { favorites, loading } = useFavorites();

  // Transform favorites to match ProductCard format
  const wishlistItems = favorites.map(fav => {
    const discount = fav.product.dis && fav.product.dis !== 'no' ? parseFloat(fav.product.dis) : null;
    const discountedPrice = discount && fav.product.price ? (fav.product.price * (1 - discount / 100)).toFixed(2) : null;
    
    return {
      id: fav.product.id,
      title: fav.product.name,
      price: discount ? discountedPrice : fav.product.price, // Show discounted price if discount exists, otherwise original
      originalPrice: discount ? fav.product.price : null, // Original price only if discount exists
      discount,
      currency: fav.product.currency,
      image: fav.product.img || fav.product.images?.[0] || 'https://via.placeholder.com/300x240',
      brand: fav.product.brand?.name,
      seller: { id: fav.product.brand_id || 1, name: fav.product.brand?.name || 'Seller' }
    };
  });

  if (loading) {
    return (
      <Container>
        <Title>My Wishlist</Title>
        <EmptyWishlist>
          <div style={{ textAlign: 'center', color: '#6b7280' }}>Loading...</div>
        </EmptyWishlist>
      </Container>
    );
  }

  if (wishlistItems.length === 0) {
    return (
      <Container>
        <Title>My Wishlist</Title>
        <EmptyWishlist>
          <EmptyIcon>❤️</EmptyIcon>
          <EmptyTitle>Your wishlist is empty</EmptyTitle>
          <EmptyText>Add products to your wishlist to save them for later.</EmptyText>
          <ShopButton to="/">Start Shopping</ShopButton>
        </EmptyWishlist>
      </Container>
    );
  }

  return (
    <Container>
      <Title>My Wishlist ({wishlistItems.length})</Title>
      <ProductsGrid>
        {wishlistItems.map(product => (
          <ProductCard key={product.id} product={product} />
        ))}
      </ProductsGrid>
    </Container>
  );
}

export default Wishlist;
